import React, { useEffect, useRef, useState } from "react";
import { LuImage } from "react-icons/lu";
import { useTranslations } from "@/TranslationContext";
import axios from "axios";
import Image from "next/image";

export interface MediaUploaderMedia {
  uuid?: string;
  file?: File;
  isUploading?: boolean;
  url?: string;
  previewUrl?: string;
  failed?: boolean;
  mediaType?: "image" | "video";
}

interface MediaUploaderProps {
  mediaList: MediaUploaderMedia[];
  maxMediaFiles: number;
  onMediaChange?: (media: MediaUploaderMedia[]) => void;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  mediaList,
  maxMediaFiles,
  onMediaChange,
}) => {
  const [loadedMedia, setLoadedMedia] =
    useState<MediaUploaderMedia[]>(mediaList);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoadedMedia(mediaList);
  }, [mediaList]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setLoadedMedia((prevMedia) => [
          ...prevMedia,
          {
            file,
            previewUrl: reader.result as string,
            isUploading: true,
            failed: false,
            mediaType: file.type.includes("video") ? "video" : "image",
          },
        ]);
      };
      reader.readAsDataURL(file);
      uploadFile({ file });
    }
  };

  const t = useTranslations();

  const addMedia = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploadFile = async (media: MediaUploaderMedia) => {
    let success = false;
    let data = null;
    try {
      const formData = new FormData();
      formData.append("file", media.file);
      const response = await axios.post(
        "/api/agent-admin/post/upload-photo",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      success = true;
      data = {
        uuid: response.data.data.uuid,
      };
    } catch (error) {}
    setLoadedMedia((prevMedia) =>
      prevMedia.map((m) =>
        m.file === media.file
          ? {
              ...m,
              isUploading: false,
              failed: !success,
              uuid: success ? data.uuid : null,
            }
          : m
      )
    );
  };

  useEffect(() => {
    if (onMediaChange) {
      onMediaChange(loadedMedia);
    }
  }, [loadedMedia]);

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
  };

  const removeMedia = (media: MediaUploaderMedia) => {
    setLoadedMedia((prevMedia) =>
      prevMedia.filter((m: MediaUploaderMedia) => m.uuid !== media.uuid)
    );
  };

  return (
    <div className="grid grid-cols-2 gap-[20px] w-[420px]">
      {loadedMedia.map((media, index) => (
        <div
          key={index}
          className="grid place-content-center w-[200px] aspect-square relative bg-gray-200 rounded cursor-pointer hover:bg-gray-300 overflow-hidden"
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <span className="text-gray-500">Loading...</span>
            </div>
          )}
          <Image
            src={media.previewUrl}
            alt="media"
            className={`object-cover w-full h-full rounded ${
              loading ? "opacity-0" : "opacity-100"
            } transition-opacity duration-300`}
            width={1500}
            height={1500}
            onLoadingComplete={handleImageLoad} // For <Image> from next/image
            onError={handleImageError} // Handle errors
          />
          {media.isUploading && !media.failed && (
            <div className="absolute inset-0 bg-black/60 text-white grid place-content-center">
              <svg
                className="animate-spin h-5 w-5 mr-3 ..."
                viewBox="0 0 24 24"
              ></svg>
              Uploading...
            </div>
          )}
          {media.failed && (
            <div className="absolute inset-0 bg-red-500 bg-opacity-50 text-white grid place-content-center">
              <span>Failed to upload</span>
              <button
                className="bg-white text-black"
                onClick={() =>
                  setLoadedMedia((prevMedia) =>
                    prevMedia.filter((m) => m.file !== media.file)
                  )
                }
              >
                Retry
              </button>
            </div>
          )}
          <div className="absolute top-0 left-0 text-center p-1 bg-gray-800 text-[9px] text-white">
            {media.mediaType === "image" ? "PHOTO" : "VIDEO"}
          </div>

          <button
            onClick={() => {
              removeMedia(media);
            }}
            className="absolute bottom-0 flex space-x-2 text-xs right-0 bg-red-500 text-white p-1"
          >
            Remove
          </button>
        </div>
      ))}
      {loadedMedia.length < maxMediaFiles && (
        <div
          className="grid place-content-center  w-[200px] aspect-square bg-gray-200 rounded cursor-pointer hover:bg-gray-300"
          onClick={addMedia}
        >
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            // accept=".jpg,.jpeg,.png,.webp,.mp4"
            accept=".jpg,.jpeg,.png"
          />
          <div className="text-center items-center">
            <div className="text-center grid place-content-center">
              <LuImage className="w-8 h-8" />
            </div>

            <div dangerouslySetInnerHTML={{ __html: t.addMedia }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
